// File sets management
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import fileSetApi from '../services/fileSetApi';
import Loading from '../components/Loading';

const FileSets = () => {
  const [fileSets, setFileSets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewFileSetForm, setShowNewFileSetForm] = useState(false);
  const [newFileSetName, setNewFileSetName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch file sets on component mount
  useEffect(() => {
    const fetchFileSets = async () => {
      try {
        setIsLoading(true);
        const data = await fileSetApi.getFileSets();
        // console.log(data);
        setFileSets(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching file sets:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileSets();
  }, []);

  // Format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Create a new file set
  const handleCreateFileSet = async (e) => {
    e.preventDefault();
    
    if (!newFileSetName.trim()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      const newFileSet = await fileSetApi.createFileSet({ name: newFileSetName });
      
      // Add the new file set to the list
      setFileSets([newFileSet, ...fileSets]);
      
      // Reset form
      setNewFileSetName('');
      setShowNewFileSetForm(false);
    } catch (err) {
      setError(err.message);
      console.error('Error creating file set:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading file sets..." />;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Sets</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage files grouped by website/project.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewFileSetForm(true)}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          New File Set
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">Error: {error}</p>
            </div>
          </div>
        </div>
      )}

      {/* New File Set Form */}
      {showNewFileSetForm && (
        <div className="bg-white shadow-md rounded-lg p-4 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Create New File Set</h2>
          <form onSubmit={handleCreateFileSet}>
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                File Set Name
              </label>
              <input
                type="text"
                id="name"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                value={newFileSetName}
                onChange={(e) => setNewFileSetName(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowNewFileSetForm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isSubmitting ? 'Creating...' : 'Create File Set'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* File Sets List */}
      {fileSets.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No file sets</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating a new file set.
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowNewFileSetForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              New File Set
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {fileSets.map((fileSet) => (
              <li key={fileSet.fileSetId}>
                <Link to={`/advanced/file-sets/${fileSet.fileSetId}`} className="block hover:bg-gray-50">
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <p className="text-base font-medium text-blue-600 truncate">
                        {fileSet.name}
                      </p>
                      {/* <div className="ml-2 flex-shrink-0 flex">
                        <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {fileSet.userId || 'User'}
                        </p>
                      </div> */}
                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        Created on {formatDate(fileSet.createdAt)}
                      </p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileSets;